import {createFileRoute} from "@tanstack/react-router"
import {useTranslation} from "react-i18next";
import {
    ChangePasswordRequest,
    changePasswordVerb,
    PasswordChange,
    SelfPatch,
    selfTqk,
    SortOrder,
} from "../../../rpc/webrpc-client.gen.ts";
import {SuspenseDialog} from "../../../components/SuspenseDialog.tsx";
import {useForm} from "@tanstack/react-form";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
    Box,
    Button,
    Divider,
    FormControl,
    FormLabel,
    IconButton,
    Input as JoyInput,
    Stack,
    ToggleButtonGroup
} from "@mui/joy";
import {ParseKeys} from "i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowsSpin, faSortAsc, faSortDesc} from "@fortawesome/pro-regular-svg-icons";
import {HCBox} from "../../../components/HCBox.tsx";
import {Alert} from "@mui/material";
import {useCallback} from "react";
import {useAuthenticator} from "../../../auth/authenticator.tsx";
import {isString} from "lodash";
import {NumericFormat} from "react-number-format";
import {AgentPreferences, defaultPrefs} from "../../../util/defaultPrefs.tsx";
import {useBomb, useToaster} from "../../../bomb/bomb.tsx";
import {useFullSelf, useSelf} from "../../../agent/useSelf.ts";
import {errorize} from "../../../bomb/errorize.tsx";
import {keyify} from "../../../util/arutil.tsx";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";
import {useMothership} from "../../../tq/tqx.tsx";

// export const DEFAULT_PREFS: AgentPreferences = {
//     agentSortOrder: SortOrder.enum.DESC,
//     truckSortOrder: SortOrder.enum.DESC,
//     bundleSortOrder: SortOrder.enum.DESC,
//     wasteSortOrder: SortOrder.enum.DESC,
//     // sleepSeconds: 5 * 60,
//     sleepSeconds: 4 * 60 * 60,
// } as const;


interface PasswordChangeForm {
    targetLoginName: string;
    prev: string;
    next: string;
    confirm: string;
}

function PasswordChangeView() {
    const {t} = useTranslation();
    const mama = useMothership();
    const bomb = useBomb();
    const auther = useAuthenticator();
    const {data: self} = useSelf();
    const canAdmin = self?.canAdmin;
    const {mutateAsync: performPasswordChange} = useMutation({
        mutationKey: [changePasswordVerb],
        mutationFn: (args: ChangePasswordRequest) => mama.changePassword(args),
    });
    const toaster = useToaster();
    const defaultValues: PasswordChangeForm = {targetLoginName: "", prev: "", next: "", confirm: ""};
    const handleSubmit = useCallback(({value}: { value: PasswordChangeForm }) => {
        if (value.next !== value.confirm) {
            return Promise.resolve("passwordMismatch");
        }
        return Promise.resolve(value.targetLoginName)
            .then(targetLoginName => {
                return targetLoginName
                    ? mama.watchAgentByLogin({login: targetLoginName})
                        .then(tqrv => tqrv.agent?.id)
                    : self?.id;
            })
            .then(targetId => {
                if (!targetId) {
                    throw new Error("no target id");
                }
                const mutargs: PasswordChange = {
                    targetId,
                    oldPassword: value.prev,
                    newPassword: value.next,
                };
                return mutargs;
            })
            .then(mutargs => performPasswordChange({food: mutargs}))
            .then(({authtoken}) => {
                if (authtoken) {
                    auther.setJwt(authtoken);
                }
                toaster.raise({
                    decos: {color: "success", variant: "soft"},
                    children: t("userPrefs.passwordChangeSuccess")
                });
                return null;
            })
            .catch((xc: unknown) => {
                const err = errorize(xc);
                return err.name;
            });
    }, [auther, mama, performPasswordChange, self?.id, t, toaster]);

    const form = useForm({
        defaultValues,
        validators: {
            onSubmitAsync: handleSubmit,
        }
    });

    return <Box sx={{mx: 2}}>
        <Divider color="primary">{t("userPrefs.changePasswordDivider")}</Divider>
        <form
            onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                form.handleSubmit().catch(bomb);
            }}
        >
            {canAdmin
                ? <form.Field
                    name="targetLoginName"
                    children={(field) =>
                        <>
                            <FormControl color="primary">
                                <FormLabel>{t("userPrefs.targetLoginName")}</FormLabel>
                                <JoyInput
                                    value={field.state.value}
                                    onChange={(evt) => {
                                        field.handleChange(evt.target.value)
                                    }}
                                />
                            </FormControl>
                        </>}
                /> : null}
            <form.Field
                name="prev"
                children={(field) =>
                    <>
                        <FormControl color="primary">
                            <FormLabel>{t("userPrefs.oldPassword")}</FormLabel>
                            <JoyInput
                                value={field.state.value}
                                onChange={(evt) => {
                                    field.handleChange(evt.target.value)
                                }}
                                type="password"
                            />
                        </FormControl>
                    </>}
            />
            <form.Field
                name="next"
                validators={{
                    onSubmit: ({value}) => {
                        if (!value) {
                            return "emptyPassword";
                        }
                        return undefined;
                    },
                }}
                children={(field) => {
                    return <><FormControl color="primary">
                        <FormLabel>{t("userPrefs.newPassword")}</FormLabel>
                        <JoyInput
                            value={field.state.value}
                            onChange={(evt) => {
                                field.handleChange(evt.target.value)
                            }}
                            type="password"
                        />
                    </FormControl>
                        {keyify(field.state.meta.errors).map(kerr => {
                            if (kerr.elem) {
                                const tk = `userPrefs.validationError.${kerr.elem}` as ParseKeys;
                                const msg = t(tk);
                                return <Alert severity="error" key={kerr.key}>{msg}</Alert>;
                            } else {
                                return null;
                            }
                        })}</>;
                }}
            />
            <form.Field
                name="confirm"
                children={(field) =>
                    <FormControl color="primary">
                        <FormLabel>{t("userPrefs.confirmPassword")}</FormLabel>
                        <JoyInput
                            value={field.state.value}
                            onChange={(evt) => {
                                field.handleChange(evt.target.value)
                            }}
                            type="password"
                        />
                    </FormControl>
                }
            />
            <HCBox>
                <form.Subscribe
                    selector={(state => [state.errors])}
                    children={([errors]) => {
                        const actualErrors = (errors ?? []).filter<string>((maybe) => {
                            return isString(maybe);
                        });
                        return keyify(actualErrors)
                            .map(kelem => {
                                const err = kelem.elem;
                                const tk = `userPrefs.validationError.${err}` as ParseKeys;
                                return <Alert key={kelem.key} severity="error">{t(tk)}</Alert>;
                            });
                    }}/>
            </HCBox>
            <HCBox>
                <form.Subscribe
                    selector={(state) => [state.canSubmit, state.isSubmitting]}
                    children={([canSubmit, isSubmitting]) => (
                        <Button type="submit" disabled={!canSubmit} size="lg"
                                sx={{py: 1, px: 8, mt: 1.8, mb: 3.2}}>
                            {isSubmitting
                                ? <FontAwesomeIcon icon={faArrowsSpin} size="2x" spin={true}
                                                   style={{color: "white", mixBlendMode: "difference"}}/>
                                : t("userPrefs.change")}
                        </Button>
                    )}
                />
            </HCBox>
        </form>
    </Box>;
}

function SelfPreferencesEditor() {
    const {t} = useTranslation();
    const mama = useMothership();
    const tqc = useQueryClient();
    const bomb = useBomb();
    const toaster = useToaster();
    const {data: fullSelf, isSuccess} = useFullSelf();
    const prefZprv = AgentPreferences.safeParse(fullSelf?.prefs);
    const prefs = prefZprv.success ? prefZprv.data : defaultPrefs;
    const performSubmit = useCallback(({value}: { value: AgentPreferences }) => {
        const patch: SelfPatch = {prefs: value};
        return mama.patchSelf({patch})
            .then(() => {
                toaster.raise({
                    decos: {color: "success", variant: "soft"},
                    children: t("userPrefs.prefsChangeSuccess")
                });
            })
            .then(() => tqc.invalidateQueries({queryKey: selfTqk}))
            .catch(bomb);
    }, [bomb, mama, t, toaster, tqc]);

    const form = useForm({
        defaultValues: prefs,
        validators: {
            onSubmitAsync: performSubmit,
        }
    });

    if (!isSuccess) {
        return <SuspenseDialog message="loading preferences"/>;
    }

    return <PageShell>
        <PageHeader><TopBar/></PageHeader>
        <PageBody>
            <Stack direction="column">
                <PasswordChangeView/>

                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        form.handleSubmit().catch(bomb);
                    }}
                >
                    <Divider color="primary">{t("userPrefs.sortOrderDivider")}</Divider>
                    <Stack direction="row" sx={{justifyItems: "space-evenly"}}>
                        {["agent", "truck", "bundle", "waste"].map(flavor => {
                            const fieldName = `${flavor}SortOrder` as "agentSortOrder" | "truckSortOrder" | "bundleSortOrder" | "wasteSortOrder";
                            const labelKey = `userPrefs.${flavor}` as ParseKeys;
                            return <Box key={fieldName} sx={{flex: 1}}>
                                <form.Field
                                    name={fieldName}
                                    children={(field) =>
                                        <FormControl orientation="horizontal" sx={{px: 2}} color="primary">
                                            <FormLabel>{t(labelKey)}</FormLabel>
                                            <ToggleButtonGroup value={field.state.value}
                                                               color="primary"
                                                               onChange={(_, next) => {
                                                                   const nv = next ?? defaultPrefs[fieldName]
                                                                   field.handleChange(nv);
                                                               }}
                                            >
                                                <IconButton value={SortOrder.enum.ASC}>
                                                    <FontAwesomeIcon size="2x" icon={faSortAsc}/>
                                                </IconButton>
                                                <IconButton value={SortOrder.enum.DESC}>
                                                    <FontAwesomeIcon size="2x" icon={faSortDesc}/>
                                                </IconButton>
                                            </ToggleButtonGroup>
                                        </FormControl>
                                    }
                                />
                            </Box>;
                        })}
                    </Stack>
                    <Divider color="primary">{t("userPrefs.sleepDivider")}</Divider>
                    <HCBox>
                        <form.Field
                            name="sleepSeconds"
                            children={(field) =>
                                <Stack direction="row" sx={{alignItems: "center"}}>
                                    <NumericFormat
                                        value={Math.round(field.state.value / 60)}
                                        onValueChange={(values) => {
                                            field.handleChange(Math.round((values.floatValue ?? 0) * 60));
                                        }}
                                        customInput={JoyInput}
                                        slotProps={{input: {type: "number", style: {textAlign: "right"}}}}
                                        sx={{
                                            maxWidth: "8rem",
                                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                                display: "none"
                                            },
                                            "& input[type=number]": {
                                                MozAppearance: "textfield"
                                            }
                                        }}
                                        valueIsNumericString={true}
                                    />
                                    <Box sx={{ml: 1}}>{t("duration.min")}</Box>
                                </Stack>
                            }
                        />
                    </HCBox>
                    <HCBox>
                        <form.Subscribe
                            selector={(state) => [state.canSubmit, state.isSubmitting]}
                            children={([canSubmit, isSubmitting]) => (
                                <Button type="submit" disabled={!canSubmit} size="lg"
                                        sx={{py: 1, px: 8, mt: 1.8, mb: 3.2}}>
                                    {isSubmitting
                                        ? <FontAwesomeIcon icon={faArrowsSpin} size="2x" spin={true}
                                                           style={{color: "white", mixBlendMode: "difference"}}/>
                                        : t("userPrefs.change")}
                                </Button>
                            )}
                        />
                    </HCBox>
                </form>
            </Stack>
        </PageBody>
    </PageShell>;
}

export const Route = createFileRoute("/_auth/self/prefs")({
    component: SelfPreferencesEditor,
});
